import { useMemo, useCallback, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { useQuery } from '@apollo/react-hooks';

import {
  SelectField,
  Button,
  MESSAGES_ADD_ON_ID,
  getProductSubscriptionLineOfSubscriptionLines,
  Tooltip,
  LoadingIndicator,
  ErrorMessage,
  Modal,
} from '../../../../../../common';
import theme from '../../../../../../global/style/theme';
import { ReactComponent as ZoneIcon } from '../../../../../../assets/icons/zoneLarge.svg';
import { ReactComponent as ClockIcon } from '../../../../../../assets/icons/clock.svg';
import { useAddonsOverview } from '../../../addonsOverviewContext';
import { GET_DETAILED_ZONE_BY_ID } from '../api';

const StyledErrorMessage = styled(ErrorMessage)`
  width: 100%;
  margin-top: 2rem;
`;

const StyledLoadingIndicator = styled(LoadingIndicator)``;

const Container = styled.div`
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.stroke};
  padding: 1.6rem 1.2rem;

  @media only screen and (min-width: ${({ theme }) =>
      theme.breakpoints.medium}px) {
    display: grid;
    grid-gap: 1rem 2rem;
    grid-auto-columns: 1fr 2fr;
    grid-template-areas: 'name name' 'input footer';
  }

  @media only screen and (min-width: ${({ theme }) =>
      theme.breakpoints.extraLarge}px) {
    display: grid;
    grid-auto-columns: 2fr 1fr 2fr;
    grid-template-areas: 'name input footer';
    grid-gap: 2rem;
  }
`;

const Header = styled.div`
  grid-area: name;
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: min-content 1fr;
  grid-gap: 1rem;
  align-items: center;
`;

const Name = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1.8rem;
  font-weight: 600;
  line-height: 1.33;
`;

const StyledSelectField = styled(SelectField)`
  grid-area: input;
  margin: 1rem 0;
`;

const OpeningHoursButtonWrapper = styled.div`
  display: grid;
  grid-template-columns: min-content max-content;
  grid-auto-flow: column;
  align-items: center;
  grid-gap: 0 1rem;
`;

const Footer = styled.div`
  grid-area: footer;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  grid-gap: 1rem;
  grid-template-columns: 1fr 1fr;

  @media only screen and (min-width: ${({ theme }) =>
      theme.breakpoints.medium}px) {
    justify-content: space-around;
  }
`;

const StyledClockIcon = styled(ClockIcon)`
  display: none;
  @media only screen and (min-width: ${({ theme }) =>
      theme.breakpoints.small}px) {
    display: flex;
  }
`;

const ActiveLabel = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1.6rem;
  font-weight: 500;
  line-height: 1.33;
  text-align: center;
  color: ${({ theme }) => theme.dark300};
`;

const ModalDescription = styled.p`
  color: ${({ theme }) => theme.dark300};
  font-size: 1.4rem;
  font-weight: 500;
  line-height: 1.4;
`;

const ZoneCard = ({
  zone,
  locationId,
  messageProfiles,
  messageProfilesError,
  messageProfilesLoading,
  fields,
  handleEditOpeningHours,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canBuyNonePaidAddons } = useAddonsOverview();
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const [isOpen, setIsOpen] = useState(false);

  const {
    data: detailedZoneData,
    error: detailedZoneError,
    loading: detailedZoneLoading,
  } = useQuery(GET_DETAILED_ZONE_BY_ID, {
    variables: { zoneId: zone.id },
  });

  const profileOptions = useMemo(
    () => [
      ...(messageProfiles?.map((profile) => ({
        value: profile.id,
        label: profile.name,
      })) ?? []),
      {
        value: '-1',
        label: t(
          'broadcasts:card:actions.zoneOverview.modal.profile.noProfileOption'
        ),
      },
    ],
    [messageProfiles, t]
  );

  const hasCurrentSubscription = useMemo(
    () => detailedZoneData?.zone?.currentSubscription,
    [detailedZoneData?.zone?.currentSubscription]
  );

  const goToBuyAddonPage = useCallback(() => {
    navigate(
      `/organizations/${
        detailedZoneData?.zone?.location.organization.id
      }/locations/${detailedZoneData.zone.location.id}/zones/${
        detailedZoneData?.zone?.id
      }/purchase/add-ons/0?addonId=${encodeURIComponent(MESSAGES_ADD_ON_ID)}`
    );
  }, [navigate, detailedZoneData?.zone]);

  const handleNavigationZone = useCallback(() => {
    if (hasCurrentSubscription) {
      goToBuyAddonPage();
    } else {
      setIsOpen(true);
    }
  }, [goToBuyAddonPage, hasCurrentSubscription]);

  const goToBuySubscriptionPage = useCallback(() => {
    navigate(
      `/organizations/${detailedZoneData?.zone?.location?.organization.id}/locations/${detailedZoneData?.zone?.location?.id}/zones/${detailedZoneData?.zone?.id}/purchase/0`
    );
  }, [
    detailedZoneData?.zone?.id,
    detailedZoneData?.zone.location.id,
    detailedZoneData?.zone.location.organization.id,
    navigate,
  ]);

  const hasPaidProduct = useMemo(() => {
    const productSubscriptionLine =
      getProductSubscriptionLineOfSubscriptionLines(
        detailedZoneData?.zone?.currentSubscription?.subscriptionLines
      );
    return productSubscriptionLine?.product?.type === 'paid';
  }, [detailedZoneData?.zone?.currentSubscription?.subscriptionLines]);

  const hasAddon = useMemo(
    () =>
      detailedZoneData?.zone?.currentSubscription?.subscriptionLines?.find(
        (subscriptionLine) =>
          subscriptionLine.product.isAddon &&
          subscriptionLine.product.categoryId === MESSAGES_ADD_ON_ID
      ),
    [detailedZoneData?.zone?.currentSubscription?.subscriptionLines]
  );

  const renderBuyContent = useMemo(() => {
    if (detailedZoneError) {
      return <StyledErrorMessage error={detailedZoneError} />;
    }
    if (detailedZoneLoading) {
      return <StyledLoadingIndicator size="medium" thickness="medium" />;
    }
    if (hasAddon) {
      return <ActiveLabel>{t('glossary:active')}</ActiveLabel>;
    }
    if (hasPaidProduct || canBuyNonePaidAddons) {
      return (
        <Button onClick={handleNavigationZone} type="button" size="medium">
          {t('glossary:buy')}
        </Button>
      );
    }
    return (
      <Button
        onClick={handleNavigationZone}
        isDisabled
        type="button"
        size="medium"
      >
        <Tooltip label={t('addons:overview.nonePaidProduct')}>
          {t('glossary:buy')}
        </Tooltip>
      </Button>
    );
  }, [
    canBuyNonePaidAddons,
    detailedZoneError,
    detailedZoneLoading,
    handleNavigationZone,
    hasAddon,
    hasPaidProduct,
    t,
  ]);

  return (
    <Container>
      <Header>
        <ZoneIcon />
        <Name>{zone.name}</Name>
      </Header>
      {fields
        .filter((field) => field.zoneId === zone.id)
        .map((zoneField) => {
          return (
            <StyledSelectField
              key={zoneField.id}
              control={control}
              name={`messageProfiles_${locationId}[${fields.findIndex(
                (field) => field.zoneId === zone.id
              )}].messageProfile`}
              isLoading={messageProfilesLoading}
              error={
                messageProfilesError ||
                errors?.messageProfiles?.[
                  fields.findIndex((field) => field.zoneId === zone.id)
                ]?.messageProfile?.message
              }
              disabled={messageProfilesError}
              options={profileOptions}
            />
          );
        })}
      <Footer>
        <OpeningHoursButtonWrapper>
          <StyledClockIcon color={theme.sweetOrange} />
          <Button
            emphasis="secondary"
            size="medium"
            highlightColor="stroke"
            onClick={handleEditOpeningHours}
          >
            {t('glossary:openingHours')}
          </Button>
        </OpeningHoursButtonWrapper>
        {renderBuyContent}
      </Footer>
      <Modal
        isOpen={isOpen}
        hideHeader
        onConfirm={goToBuySubscriptionPage}
        onClose={setIsOpen}
        confirmText={t('common:button.buyNow')}
        confirmHighlightGradient="nightfall"
      >
        <ModalDescription>{t('addons:needSubscription')}</ModalDescription>
      </Modal>
    </Container>
  );
};

export default ZoneCard;
